import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { h, render } from "preact";
import type { ComponentChildren, FunctionComponent, HTMLAttributes } from "preact";
import { useMergedClassName } from "../utils.ts";

export type OverlayHookOptions = {
    children?: ComponentChildren;
} & HTMLAttributes<HTMLDivElement>;

export type OverlayProps = OverlayHookOptions & {
    isOpen?: boolean;
};

type OverlayRootProps = Omit<OverlayProps, "isOpen">;

const OverlayRoot: FunctionComponent<OverlayRootProps> = (props: OverlayRootProps) => {
    const className = useMergedClassName("tinyui_overlay_layer", props.class);
    return (
        <div {...props} class={className}>
            {props.children}
        </div>
    );
};

export const useOverlay = (props: OverlayHookOptions = {}) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // render overlay
            if (rootRef.current === null) {
                rootRef.current = document.createElement("div");
                rootRef.current.className = "tinyui_overlay_container";
                document.body.appendChild(rootRef.current);
            }
            const vnode = h(OverlayRoot, props);
            render(vnode, rootRef.current);
        } else {
             // clean when unmount
             if (rootRef.current !== null) {
                 render(null, rootRef.current);
                 document.body.removeChild(rootRef.current);
                 rootRef.current = null;
             }
        }
    }, [isOpen]);

    useEffect(() => {
        // cleanup on component unmount only
        return () => {
            if (rootRef.current !== null) {
                render(null, rootRef.current);
                if (document.body.contains(rootRef.current)) {
                    document.body.removeChild(rootRef.current);
                }
                rootRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (isOpen && rootRef.current) {
            // update render overlay
            const vnode = h(OverlayRoot, props);
            render(vnode, rootRef.current);
        }
    }, [props]);

    const api = useMemo(() => {
        return {
            setIsOpen,
            open: () => setIsOpen(true),
            close: () => setIsOpen(false),
        };
    }, [setIsOpen]);
    return api;
};

export const Overlay: FunctionComponent<OverlayProps> = (props: OverlayProps = {}) => {
    const api = useOverlay(props);

    useEffect(() => {
        if (props.isOpen) {
            api.open();
        } else {
            api.close();
        }
    }, [props.isOpen]);

    return null;
};

export default Overlay;
